import React, { useState, useEffect } from 'react';
/**
 * `RenewalsSummary` renders the summary step for the renewals journey.
 *
 * Responsibilities:
 * - Renders the shared `Summary` component for vehicle, customer details, and
 *   selected quote.
 * - Allows choosing a payment method by selecting a variant (single/monthly/
 *   subscription/payment assist).
 * - Enforces customer acceptance (non-call-centre) and optional mandatory
 *   document download gating.
 * - Routes to the appropriate next page (single payment, direct debit, or
 *   Payment Assist redirect).
 */
import { useNavigate } from 'react-router-dom';
import {
  PageAbsolutePath,
  Journey,
  PageRelativePath
} from '../../../consts/Common';
import {
  calculateTotals,
  getSelectedProducts,
  //getNoOfInstallments,
  //getProductInstalmentTotalPriceByPaymentMethod,
  //getSelectedProducts,
  getSelectedVariant
} from '../../common/ProductMethods';
import {
  PaymentAssistBeginResponse,
  PaymentAssistCustomer,
  PaymentAssistRequest,
  ProductType,
  Totals,
  DocumentTreeItem,
  DocumentItem
} from '../../common/CommonTypes';
import { PaymentMethod } from '../../common/Constants';
//import { Force24Helper } from '../../common/Force24Helper';
import PageHeader from '../../common/PageHeader';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import Summary, {
  SummaryCustomerAcceptance
} from '../../common/Summary/Summary';
import { useJourney } from '../Layout';
import { useTranslation } from 'react-i18next';
import { PaymentApi } from '../../../api/PaymentApi';
import { useAppSelector } from '../../../app-state/Hooks';

const RenewalsSummary = (): JSX.Element => {
  const navigate = useNavigate();
  const {
    onPageChange,
    journey,
    onUpdateProducts,
    onProductDocumentsDownloaded
  } = useJourney();
  const { t } = useTranslation();

  const paymentOptionRequiredError = t('summary.payment-option-required-error');
  const buttonContinuePayment = t('summary.button-continue-payment');
  const buttonBackText = t('summary.button-back-text');

  const {
    personalDetail,
    vehicle,
    products,
    policyStartDate,
    singlePaymentDetails,
    documents,
    productDocumentsDownloaded
  } = journey;

  console.log(documents);
  const [agreedTermsAndConditions, setAgreedTermsAndConditions] =
    useState(false);
  console.log('Agreed T&Cs:', agreedTermsAndConditions);

  const [paymentError, setPaymentError] = useState('');
  const [documentError, setDocumentError] = useState('');

  //const noOfInstallments = getNoOfInstallments(products);
  const site = useAppSelector((s) => s); // TODO: consider render optimization
  const brand = site.brand;
  const appConfig = useAppSelector((s) => s.appConfig);

  const cms = site.content.summaryPage;
  const isCallCenter = appConfig.journeyType === Journey.CallCentre;
  const isDocumentDownloadMandatory = brand.isDocumentDownloadMandatory;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //let selectedQuotes: ProductType[] = [];
  //if (products && products !== undefined)
  //  selectedQuotes = getSelectedProducts(products);

  const getDocumentsPaths = (documents: DocumentTreeItem[]): string[] => {
    let paths: string[] = [];
    documents.forEach((element) => {
      if (element.path) paths.push(element.path);
      else {
        if (element.children) {
          const childPaths = getDocumentsPaths(element.children);
          paths = paths.concat(childPaths);
        }
      }
    });
    return paths;
  };

  const getCmsDocumentsPaths = (documents: DocumentItem[]): string[] => {
    const paths: string[] = [];
    documents.forEach((element) => {
      if (element.documentPath) paths.push(element.documentPath);
    });
    return paths;
  };

  const productDocuments = isDocumentDownloadMandatory
    ? getDocumentsPaths(documents).concat(
        getCmsDocumentsPaths(site.content.usefulDocuments)
      )
    : [];

  const updateQuotes = (
    variantId: string,
    quoteTypeId: number,
    paymentMethod: string
  ) => {
    const updated = products.map((q) =>
      q.productTypeId === quoteTypeId
        ? {
            ...q,
            selectedPaymentMethod: paymentMethod,
            productVariants: q.productVariants.map((variant) => {
              return {
                ...variant,
                isSelected: variant.variantId === variantId
              };
            })
          }
        : {
            ...q,
            selectedPaymentMethod: undefined,
            productVariants: q.productVariants.map((variant) => {
              return {
                ...variant,
                isSelected: false
              };
            })
          }
    );

    onUpdateProducts(updated);
  };

  const onBack = () => {
    if (isCallCenter === true) navigate(PageRelativePath.PersonalDetails);
    else navigate(PageRelativePath.Quote);
  };

  const selectedProducts = getSelectedProducts(products);
  const totals: Totals = calculateTotals(selectedProducts, 1);

  const callPaymentAssistRedirect = () => {
    const token = journey.token || site.user.token;

    const { billingAddress } = singlePaymentDetails;

    const selectedVariant = selectedProducts[0].productVariants.find(
      (v) => v.isSelected
    );

    const paymentInfo: PaymentAssistCustomer = {
      amount: totals.selectedTotalPrice,
      productVariant: selectedVariant?.variantId ?? '',
      surname: personalDetail.surname,
      firstName: personalDetail.firstName,
      address1: billingAddress.addressLine1 ?? '',
      address2: billingAddress.addressLine2 ?? '',
      city: billingAddress.townOrCity ?? '',
      postCode: billingAddress.postCode ?? '',
      county: billingAddress.region ?? '',
      phone: personalDetail.phone,
      email: personalDetail.email,
      vrn: vehicle?.vehicleDetails?.vrn ?? ''
    };

    const paymentAssistRequest: PaymentAssistRequest = {
      paymentInfo,
      site: {
        user: site.user,
        brand: site.brand,
        content: site.content,
        ...appConfig
      },
      journey,
      journeyType: appConfig.journeyType
    };

    const controller = new AbortController();

    const successCallback = (data: PaymentAssistBeginResponse) => {
      window.localStorage.setItem('redirectToPA', 'true');
      window.location.href = data.url;
    };

    const failureCallback = () => {
      console.log('failure');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      navigate(PageRelativePath.PaymentAssistFailure, {
        relative: 'path'
      });
    };

    new PaymentApi().beginPaymentAssistRequest(
      paymentAssistRequest,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onNext = () => {
    const selectedQuote = products.find((quote) => quote.isSelected === true);

    if (selectedQuote) {
      const selectedVariant = getSelectedVariant(selectedQuote.productVariants);
      console.log('Products Variant:', selectedQuote.productVariants);
      console.log(selectedVariant);

      let hasPaymentError = false;
      let hasDocumentError = false;

      if (!selectedVariant) {
        setPaymentError(paymentOptionRequiredError);
        hasPaymentError = true;
      } else {
        setPaymentError('');
      }

      if (
        isDocumentDownloadMandatory &&
        productDocuments.length > 0 &&
        !productDocumentsDownloaded
      ) {
        setDocumentError(t('summary.document-download-error'));
        hasDocumentError = true;
      } else {
        setDocumentError('');
      }

      if (hasPaymentError || hasDocumentError) {
        return;
      }

      const paymentMethod = selectedVariant?.paymentMethod;
      if (paymentMethod === PaymentMethod.Single) {
        navigate(PageRelativePath.SinglePayment);
      } else if (paymentMethod === PaymentMethod.PaymentAssist) {
        callPaymentAssistRedirect();
      } else {
        navigate(PageRelativePath.DirectDebit);
      }

      onPageChange(PageAbsolutePath.Renewals.Summary, true);
    }
  };

  const onNavigateToQuoteRemove = () => {
    const updatedProduct: ProductType[] = products.map((product) => ({
      ...product,
      selectedCoverLevelId: undefined,
      selectedPaymentMethod: undefined,
      selectedLinkedProducts: undefined,
      isSelected: false,
      productVariants: product.productVariants.map((variant) => ({
        ...variant,
        isSelected: false
      }))
    }));

    onUpdateProducts(updatedProduct);
  };

  const customerAcceptanceText: SummaryCustomerAcceptance = {
    sections: cms.acceptanceText
  };

  return (
    <div>
      <PageHeader
        title={cms.title}
        description={cms.description}
        information={cms.information}
        warning={cms.warning}
        descriptionClass='p2'
      />
      <div data-testid='container-form' className='form-left-border'>
        <Summary
          vehicle={vehicle}
          personalDetail={personalDetail}
          products={products}
          agreedTermsAndConditions={agreedTermsAndConditions}
          onUpdateAgreedTermsAndConditions={setAgreedTermsAndConditions}
          onPageChange={onPageChange}
          journeyType={appConfig.journeyType}
          policyStartDate={policyStartDate}
          error={paymentError}
          onNavigateToQuoteRemove={onNavigateToQuoteRemove}
          updateQuote={updateQuotes}
          isRenewals={true}
          showTermsAndConditions={!isCallCenter}
          personalDetailsPageUrl={PageRelativePath.PersonalDetails}
          vehiclePageUrl={PageRelativePath.VehicleDetails}
          productPageUrl={PageRelativePath.Quote}
          createPlanPageUrl=''
          customerAcceptanceText={customerAcceptanceText}
          customerAcceptanceSectionExpandedOnLoad={true}
          customerAcceptanceTickboxText={cms.acceptanceTickboxText}
          culture={appConfig.cultureCode}
          countryCode={appConfig.countryCode}
          renewalMarketingTickboxText={cms.consentToRenewalMarketingTickboxText}
          phoneNumberLabel={site.content.customerPage.phoneNumberLabel}
          productDocumentsPaths={productDocuments}
          productDocumentsDownloaded={productDocumentsDownloaded || false}
          onUpdateProductDocumentsDownloaded={onProductDocumentsDownloaded}
        />
      </div>
      <div className='error-style'>{documentError}</div>
      <div className='flex-row form-nav'>
        <ButtonBack name='btnBack' onClick={onBack} text={buttonBackText} />
        <ButtonContinue
          name='btn-see-products'
          text={buttonContinuePayment}
          onClick={onNext}
          disabled={
            isCallCenter === false && agreedTermsAndConditions === false
          }
        />
      </div>
    </div>
  );
};

export default RenewalsSummary;
